import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Card, Table, Badge, Button, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import {
  useGetFormsTeamStatusQuery,
  useLazyGetReportRoleAlignmentQuery,
  useSubmitManagerialAssessmentMutation,
} from '../../app/api';
import { RoleAlignmentResults } from './RoleAlignmentForm';
import ManagerialAssessmentForm, { ManagerialAssessmentResults } from './ManagerialAssessmentForm';
import ResultActions from './ResultActions';

// Team → Role Alignment.
//
// Who sees what, and why:
//   * DIRECT reports (depth 1) — status always; an assessment to write only for
//     those HR has actually REQUESTED one for, and their questionnaire answers
//     only where HR has separately opened that up. Two gates, set per person on
//     Tech → Forms → Role Alignment → Results, both off until set. So the normal
//     case is a manager writing an assessment WITHOUT reading what their report
//     wrote; the answers are a deliberate extra.
//   * everyone further down the chain (depth 2+) — status only. Skip-level
//     managers need to know whether the process is moving; they are not asked to
//     write assessments, and these are candid self-assessments, so the answers
//     stop at the direct manager. The API enforces all of this too (404 on the
//     answers endpoint for a non-direct manager, 403 when the gate is shut) —
//     the UI just doesn't offer it.

const brand = { navy: '#002040', blue: '#0060A0', orange: '#F09010', green: '#2E7D32', gray: '#58585b' };

function StatusBadge({ done, label, dueLabel }) {
  return done
    ? <Badge bg="" style={{ background: brand.green, fontSize: '0.7rem' }}>{label}</Badge>
    : <Badge bg="light" text="dark" style={{ fontSize: '0.7rem', border: '1px solid #ddd' }}>{dueLabel}</Badge>;
}

StatusBadge.propTypes = {
  done: PropTypes.bool,
  label: PropTypes.string.isRequired,
  dueLabel: PropTypes.string.isRequired,
};

export default function TeamRoleAlignment() {
  const { data, isFetching, error, refetch } = useGetFormsTeamStatusQuery();
  const [loadReport, { isFetching: loadingReport }] = useLazyGetReportRoleAlignmentQuery();
  const [submitAssessment, { isLoading: submitting }] = useSubmitManagerialAssessmentMutation();

  const [active, setActive] = useState(null);      // { person, submission, assessment }
  const [failure, setFailure] = useState('');
  const [done, setDone] = useState(false);
  // Which tab of the editor is on top. The assessment is what the manager is
  // here to write, so it is the default and stays the default per person.
  const [tab, setTab] = useState('assessment');

  const team = data?.team || [];
  const direct = team.filter((t) => t.is_direct_report);
  const indirect = team.filter((t) => !t.is_direct_report);
  const dueCount = data?.due_count || 0;

  const open = async (person) => {
    setFailure('');
    setDone(false);
    setTab('assessment');
    try {
      const res = await loadReport({ directoryId: person.directory_id }).unwrap();
      setActive({
        person,
        submission: res.submission,
        assessment: res.assessment,
        // False means the answers were withheld by the server, not missing.
        canViewResponses: Boolean(res.can_view_responses),
      });
    } catch (e) {
      setFailure(e?.data?.detail || 'Could not open that questionnaire.');
    }
  };

  const save = async (responses) => {
    setFailure('');
    try {
      await submitAssessment({
        subjectDirectoryId: active.person.directory_id,
        responses,
      }).unwrap();
      setDone(true);
      // Tags are inert in this slice, so the list is refreshed explicitly.
      refetch();
    } catch (e) {
      setFailure(e?.data?.detail || 'Could not save the assessment.');
    }
  };

  if (isFetching && !data) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error?.data?.detail || 'Could not load your team.'}</Alert>
      </Container>
    );
  }

  // Review window closed (Super Admin → Config). The nav item is hidden too, so
  // this is the bookmark/back-button case — say why rather than showing an empty
  // team, which would read as the org chart being wrong.
  if (data?.enabled === false) {
    return (
      <Container className="py-4">
        <h3 className="mb-2">Team Role Alignment</h3>
        <Alert variant="info">
          Manager reviews aren&apos;t open yet. Your team&apos;s questionnaire answers and the
          assessments you&apos;ll be asked for become available here once HR opens the review
          window — nothing is due from you until then.
        </Alert>
      </Container>
    );
  }

  // Not a manager: say so plainly rather than showing an empty table.
  if (!team.length) {
    return (
      <Container className="py-4">
        <h3 className="mb-2">Team Role Alignment</h3>
        <Alert variant="info">
          Nobody reports to you in the employee directory, so there is nothing to review here.
          If that looks wrong, HR maintains the reporting lines on the Manage Directory page.
        </Alert>
      </Container>
    );
  }

  // ── Assessment editor ─────────────────────────────────────────────────────
  if (active) {
    const firstName = (active.person.name || '').split(' ')[0] || 'Employee';
    // The previous filing is only offered while there is still a form to file:
    // once `done`, what is on screen is what they just submitted.
    const prior = active.assessment && !done ? active.assessment : null;

    // One pane per thing to read, built as a list so the tab bar can be dropped
    // when there is only one of them — a single lonely tab reads as a mistake.
    // The manager's own form is always first and therefore the default.
    const panes = [];
    if (!done) {
      panes.push({
        key: 'assessment',
        title: 'My assessment',
        // no-print, and on a child rather than the pane itself: the pane is
        // forced visible when printing (see app.scss) so that a tab nobody is
        // looking at still reaches the paper, and a rule on the pane would
        // fight that. A blank form on paper is not what Print is for.
        node: (
          <div className="no-print">
            {prior && (
              <Alert variant="info" className="py-2" style={{ fontSize: '0.9rem' }}>
                You already filed an assessment for {active.person.name}. Submitting again records a
                new one; the most recent is what HR sees. Your previous answers are under
                <strong> What I filed previously</strong>.
              </Alert>
            )}
            <ManagerialAssessmentForm
              subjectName={active.person.name}
              subjectDirectoryId={active.person.directory_id}
              submitting={submitting}
              // Section 1 is directory data we already hold, so it is filled in
              // rather than retyped. Every field stays editable — the directory
              // goes stale and the manager is the one who would know.
              //
              // `managed_duration` is deliberately not prefilled: the directory
              // records who someone's manager is, never since when, and a guess
              // from hire_date would be wrong for anyone who changed manager in
              // the merger, which is most of this population.
              initial={{
                employee_name: active.person.name,
                // `firm` is empty across the directory; prior_company is the
                // usable cut of which of the seven firms someone is at.
                current_firm: active.person.prior_company || '',
                current_title: active.person.title || '',
                office_location: active.person.location || '',
                assessor_name: data?.me?.name || '',
                assessor_title: data?.me?.title || '',
              }}
              onSubmit={save}
            />
          </div>
        ),
      });
    }
    if (active.canViewResponses) {
      panes.push({
        key: 'answers',
        title: `${firstName}'s answers`,
        // No card or scroll box around either results pane any more: the tab is
        // the frame, and capping a form at 460px of scroll was half the reason
        // this page was hard to read.
        node: <RoleAlignmentResults responses={active.submission?.responses} />,
      });
    }
    if (prior) {
      panes.push({
        key: 'previous',
        title: 'What I filed previously',
        node: <ManagerialAssessmentResults responses={prior.responses} />,
      });
    }

    // A tab can disappear underneath the selection — filing an assessment drops
    // both "What I filed previously" and the form itself — so the stored key is
    // only honoured while it still names a pane.
    const activeKey = panes.some((p) => p.key === tab) ? tab : panes[0]?.key;

    return (
      <Container className="py-4">
        <Button variant="link" className="px-0 mb-2" onClick={() => { setActive(null); setDone(false); }}>
          ← Back to my team
        </Button>

        <h4 className="mb-1" style={{ color: brand.navy }}>{active.person.name}</h4>
        <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
          {active.person.title || 'Role Alignment'}
        </div>

        {failure && <Alert variant="danger">{failure}</Alert>}
        {done && (
          <Alert variant="success">
            Assessment saved for {active.person.name}. It is now visible to HR alongside their
            questionnaire.
          </Alert>
        )}

        {/* Only where the answers were withheld — otherwise the tab itself says
            they are there. Written plainly so it doesn't read as a bug or as
            something the manager can go and unlock. */}
        {!active.canViewResponses && (
          <Alert variant="secondary" className="py-2 no-print" style={{ fontSize: '0.88rem' }}>
            <strong>{active.person.name} completed their Role Alignment Questionnaire
            {active.submission?.created
              ? ` on ${new Date(active.submission.created).toLocaleDateString()}`
              : ''}.</strong>{' '}
            Their answers aren&apos;t shared with you — write your assessment from your own
            view of their role. HR releases a questionnaire to a manager only when the
            process calls for it.
          </Alert>
        )}

        {/* Both buttons act on what the API handed over, not on the open tab, so
            they are above the tabs rather than inside one. Hidden when there is
            nothing to act on: no answers released and nothing filed yet would
            otherwise mean a disabled CSV and a blank sheet of paper. */}
        {(active.canViewResponses || active.assessment) && (
          <ResultActions name={active.person.name}
            submission={active.submission} assessment={active.assessment} />
        )}

        {/* Panes are mounted whether or not they are on top — the default for
            react-bootstrap, and load-bearing here: unmounting the pane would
            throw away a half-written assessment every time the manager looked
            at the questionnaire. */}
        <div className="printable-result">
          {panes.length > 1 ? (
            <Tabs id="managerial-assessment-tabs" className="mb-3 no-print"
                  activeKey={activeKey} onSelect={(k) => setTab(k || 'assessment')}>
              {panes.map((p) => (
                <Tab key={p.key} eventKey={p.key} title={p.title}>{p.node}</Tab>
              ))}
            </Tabs>
          ) : (panes[0]?.node || null)}
        </div>
      </Container>
    );
  }

  // ── Team list ─────────────────────────────────────────────────────────────
  const row = (t) => (
    <tr key={t.directory_id}>
      <td>
        <div style={{ fontWeight: 600 }}>{t.name}</div>
        <div className="text-muted" style={{ fontSize: '0.78rem' }}>{t.title || '—'}</div>
      </td>
      <td><StatusBadge done={t.role_submitted} label="Completed" dueLabel="Not yet" /></td>
      <td>
        {t.is_direct_report
          ? (
            <StatusBadge
              done={t.assessment_done}
              label="Done"
              // "Not requested" rather than "—": the manager is not behind on
              // anything, and an em-dash here reads as something missing.
              dueLabel={!t.role_submitted ? '—'
                : t.assessment_requested ? 'Due from you' : 'Not requested'}
            />
          )
          : <span className="text-muted" style={{ fontSize: '0.8rem' }}>
              {t.assessment_done ? `Done — ${t.assessment_by || 'their manager'}` : 'Their manager'}
            </span>}
      </td>
      <td className="text-end">
        {t.is_direct_report && t.role_submitted && t.assessment_requested ? (
          <Button size="sm" variant={t.assessment_done ? 'outline-secondary' : 'primary'}
                  disabled={loadingReport} onClick={() => open(t)}>
            {t.assessment_done ? 'Review' : 'Assess'}
          </Button>
        ) : (
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            {!t.is_direct_report ? 'View only'
              : !t.role_submitted ? 'Waiting on them'
                : 'Nothing due yet'}
          </span>
        )}
      </td>
    </tr>
  );

  return (
    <Container className="py-4">
      <h3 className="mb-1">Team Role Alignment</h3>
      <p className="text-muted" style={{ maxWidth: '46rem', fontSize: '0.9rem' }}>
        Who has completed the Role Alignment Questionnaire, and the Managerial Assessments HR has
        asked you for. An assessment is requested person by person, so a completed questionnaire
        does not always mean something is due from you. People further down your chain are shown
        for visibility only — their own manager assesses them.
      </p>

      {dueCount > 0 && (
        <Alert variant="warning" className="py-2" style={{ fontSize: '0.9rem' }}>
          <strong>{dueCount} assessment{dueCount === 1 ? '' : 's'} due from you.</strong>{' '}
          Each one is written after reading that person&apos;s questionnaire, which opens alongside
          the form.
        </Alert>
      )}
      {failure && <Alert variant="danger">{failure}</Alert>}

      <Card className="mb-4">
        <Card.Header style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase',
                              letterSpacing: '0.5px', color: brand.navy }}>
          My direct reports
        </Card.Header>
        <Table hover responsive className="mb-0 align-middle">
          <thead>
            <tr style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              <th>Employee</th><th>Questionnaire</th><th>Assessment</th><th />
            </tr>
          </thead>
          <tbody>
            {direct.length ? direct.map(row) : (
              <tr><td colSpan={4} className="text-center text-muted py-3">No direct reports.</td></tr>
            )}
          </tbody>
        </Table>
      </Card>

      {indirect.length > 0 && (
        <Card>
          <Card.Header style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase',
                                letterSpacing: '0.5px', color: brand.navy }}>
            Further down my chain ({indirect.length}) — visibility only
          </Card.Header>
          <Table hover responsive className="mb-0 align-middle">
            <thead>
              <tr style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                <th>Employee</th><th>Questionnaire</th><th>Assessment</th><th />
              </tr>
            </thead>
            <tbody>{indirect.map(row)}</tbody>
          </Table>
        </Card>
      )}
    </Container>
  );
}
